import ApiClient from "./ApiClient";
import {
  OrganizationDTO,
  UserDTO,
  InvitationDTO
} from "../types/Tenant";

// API URL from environment variables
const API_URL = process.env.REACT_APP_TENANT_API_URL ?? "";

// Create an instance of ApiClient
const apiClientInstance = new ApiClient(API_URL);
const apiClient = apiClientInstance.getClient();

// Health Check - GET /health
export const getHealthStatus = async (): Promise<any> => {
  try {
    const response = await apiClient.get("/health");
    return response.data;
  } catch (error) {
    console.error("Error fetching health status:", error);
    throw error;
  }
};

// Create Organization - POST /organizations
export const createOrganization = async (
  organizationData: OrganizationDTO
): Promise<OrganizationDTO> => {
  try {
    // Filter out empty fields and never send ID for creation
    const filteredData: Partial<OrganizationDTO> = {};

    // Only include fields with non-empty values
    Object.entries(organizationData).forEach(([key, value]) => {
      // Skip id field and empty values
      if (key !== 'id' && value !== null && value !== undefined && value !== '') {
        filteredData[key as keyof OrganizationDTO] = value;
      }
    });

    console.log("Sending organization data:", filteredData);
    const response = await apiClient.post("/organizations", filteredData);
    return response.data;
  } catch (error) {
    console.error("Error creating organization:", error);
    throw error;
  }
};

// Get Organizations - GET /organizations
export const getOrganizations = async (): Promise<OrganizationDTO[]> => {
  try {
    const response = await apiClient.get("/organizations");
    console.log("Organizations:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching organizations", error);
    throw error;
  }
};

// Get Organization - GET /organizations/{organizationId}
export const getOrganization = async (
  organizationId: string
): Promise<OrganizationDTO> => {
  try {
    const response = await apiClient.get(`/organizations/${organizationId}`);
    console.log("Organization:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching organization", error);
    throw error;
  }
};

// Update Organization - PUT /organizations/{organization_id}
export const updateOrganization = async (
  organization: OrganizationDTO
): Promise<OrganizationDTO> => {
  try {
    if (!organization.id) {
      throw new Error("Organization ID is required for update");
    }

    // Filter out empty fields but keep ID for update
    const filteredData: Partial<OrganizationDTO> = {};

    // Only include fields with non-empty values
    Object.entries(organization).forEach(([key, value]) => {
      // Keep id field but filter out empty values for other fields
      if (key === 'id' || (value !== null && value !== undefined && value !== '')) {
        filteredData[key as keyof OrganizationDTO] = value;
      }
    });

    console.log("Updating organization with data:", filteredData);

    const response = await apiClient.put(
      `/organizations/${organization.id}`,
      filteredData
    );

    return response.data;
  } catch (error) {
    console.error("Error updating organization:", error);
    throw error;
  }
};

// Updates an organization's subscription tier
export const updateSubscription = async (
  organization: OrganizationDTO,
  subscriptionTier: string
): Promise<OrganizationDTO> => {
  try {
    // Create a new organization object with the updated subscription tier
    const updatedOrg = {
      ...organization,
      subscription_tier: subscriptionTier
    };
    return updateOrganization(updatedOrg);
  } catch (error) {
    console.error("Error updating subscription tier:", error);
    throw error;
  }
};

// Send Invitation - POST /organizations/{organization_id}/invitations
export const sendInvitation = async (
  organizationId: string,
  invitationData: InvitationDTO
): Promise<InvitationDTO> => {
  try {
    const response = await apiClient.post(
      `/organizations/${organizationId}/invitations`,
      invitationData
    );
    return response.data;
  } catch (error) {
    console.error("Error sending invitation:", error);
    throw error;
  }
};

// Get Organization Users - GET /organizations/{organization_id}/users
export const getOrganizationUsers = async (
  organizationId: string
): Promise<UserDTO[]> => {
  try {
    const response = await apiClient.get(`/organizations/${organizationId}/users`);
    return response.data;
  } catch (error) {
    console.error("Error fetching organization users:", error);
    throw error;
  }
};

// Get Organization Invitations - GET /organizations/{organization_id}/invitations
export const getOrganizationInvitations = async (
  organizationId: string
): Promise<InvitationDTO[]> => {
  try {
    const response = await apiClient.get(`/organizations/${organizationId}/invitations`);
    return response.data;
  } catch (error) {
    console.error("Error fetching organization invitations:", error);
    throw error;
  }
};

// Resend Invitation - POST /organizations/{organization_id}/invitations/{invitation_id}/resend
export const resendInvitation = async (
  organizationId: string,
  invitationId: string
): Promise<InvitationDTO> => {
  try {
    const response = await apiClient.post(
      `/organizations/${organizationId}/invitations/${invitationId}/resend`
    );
    return response.data;
  } catch (error) {
    console.error("Error resending invitation:", error);
    throw error;
  }
};

// Rescind (Cancel) Invitation - DELETE /organizations/{organization_id}/invitations/{invitation_id}
export const rescindInvitation = async (
  organizationId: string,
  invitationId: string
): Promise<void> => {
  try {
    await apiClient.post(
      `/organizations/${organizationId}/invitations/${invitationId}/recind`
    );
  } catch (error) {
    console.error("Error rescinding invitation:", error);
    throw error;
  }
};

// Get A La Carte Subscription Plans - GET /subscriptions/a-la-carte
export const getALaCarteSubscriptions = async (): Promise<any[]> => {
  try {
    const response = await apiClient.get('/subscriptions/a-la-carte');
    return response.data.map((item: any) => ({
      subscription_name: item.subscription_name,
      subscription_price: item.subscription_price,
      subscription_stripe_id: item.subscription_stripe_id,
      key_features: item.key_features || []
    }));
  } catch (error) {
    console.error('Error fetching a la carte subscriptions:', error);
    // Return hardcoded fallback data if API is not available
    return [
      {
        subscription_name: 'Small Campaign',
        subscription_price: '$79',
        subscription_stripe_id: 'price_small_campaign',
        key_features: [
          'Single small campaign (max 100 participants)',
          'Enhanced matching algorithm',
          'Priority email support',
          'Campaign metrics & reporting'
        ]
      },
      {
        subscription_name: 'Medium Campaign',
        subscription_price: '$149',
        subscription_stripe_id: 'price_medium_campaign',
        key_features: [
          'Single medium campaign (max 250 participants)',
          'Advanced matching algorithm',
          'Priority email & chat support',
          'Advanced metrics & reporting',
          'Customizable forms'
        ]
      },
      {
        subscription_name: 'Large Campaign',
        subscription_price: '$249',
        subscription_stripe_id: 'price_large_campaign',
        key_features: [
          'Single large campaign (max 1000 participants)',
          'Advanced matching algorithm',
          'Priority email & chat support',
          'Advanced metrics & reporting',
          'Customizable forms'
        ]
      }
    ];
  }
};

// Get Annual Subscription Plans - GET /subscriptions/annual
export const getAnnualSubscriptions = async (): Promise<any[]> => {
  try {
    const response = await apiClient.get('/subscriptions/annual');
    return response.data.map((item: any) => ({
      subscription_name: item.subscription_name,
      subscription_price: item.subscription_price,
      subscription_stripe_id: item.subscription_stripe_id,
      key_features: item.key_features || []
    }));
  } catch (error) {
    console.error('Error fetching annual subscriptions:', error);
    // Return hardcoded fallback data if API is not available
    return [
      {
        subscription_name: 'Free',
        subscription_price: '$0',
        subscription_stripe_id: 'price_free',
        key_features: [
          '1 free mini campaign annually (max 25 participants)',
          'Basic matching algorithm',
          'Standard support'
        ]
      },
      {
        subscription_name: 'Small Plan (2 campaigns)',
        subscription_price: '$279',
        subscription_stripe_id: 'price_small_duo',
        key_features: [
          '2 small campaigns annually (max 100 participants each)',
          'Enhanced matching algorithm',
          'Priority support',
          'Campaign metrics & reporting'
        ]
      },
      {
        subscription_name: 'Small Plan (4 campaigns)',
        subscription_price: '$499',
        subscription_stripe_id: 'price_small_quad',
        key_features: [
          '4 small campaigns annually (max 100 participants each)',
          'Enhanced matching algorithm',
          'Priority support',
          'Campaign metrics & reporting'
        ]
      },
      {
        subscription_name: 'Medium Plan (2 campaigns)',
        subscription_price: '$429',
        subscription_stripe_id: 'price_medium_duo',
        key_features: [
          '2 medium campaigns annually (max 250 participants each)',
          'Advanced matching algorithm',
          'Priority email & chat support',
          'Advanced metrics & reporting',
          'Customizable forms'
        ]
      },
      {
        subscription_name: 'Medium Plan (4 campaigns)',
        subscription_price: '$799',
        subscription_stripe_id: 'price_medium_quad',
        key_features: [
          '4 medium campaigns annually (max 250 participants each)',
          'Advanced matching algorithm',
          'Priority email & chat support',
          'Advanced metrics & reporting',
          'Customizable forms'
        ]
      },
      {
        subscription_name: 'Large Plan (2 campaigns)',
        subscription_price: '$649',
        subscription_stripe_id: 'price_large_duo',
        key_features: [
          '2 Large Campaigns/yr',
          'Unlimited applicants',
          'Annual subscription',
          'Advanced reporting'
        ]
      },
      {
        subscription_name: 'Large Plan (4 campaigns)',
        subscription_price: '$1099',
        subscription_stripe_id: 'price_large_quad',
        key_features: [
          '4 Large Campaigns/yr',
          'Unlimited applicants',
          'Annual subscription',
          'Advanced reporting'
        ]
      }
    ];
  }
};

// Export all API methods as default
const tenantService = {
  getHealthStatus,
  createOrganization,
  getOrganizations,
  getOrganization,
  updateOrganization,
  updateSubscription,
  sendInvitation,
  resendInvitation,
  rescindInvitation,
  getOrganizationUsers,
  getOrganizationInvitations,
  getALaCarteSubscriptions,
  getAnnualSubscriptions
};

export default tenantService;
